import { useDrag } from 'CustomHooks/useDragAndDrop';
import React from 'react';
import { DragPreviewImage } from 'react-dnd';
import { CurrentStatus } from 'types/reservations';
import { blockToTime, cubicBezier, isTouchDevice, maxMin } from 'utils/helper';

const touchDevice = typeof window !== 'undefined' && isTouchDevice();

type Props = {
  color: string;
  textColor: string;
  time: string;
  guests?: number;
  status?: CurrentStatus;
  top?: number | string;
  bottom?: number | string;
  /** Optional horizontal offset (only used by shared-table multi-pill layout). */
  left?: number | string;
  right?: number | string;
  size: number;
  touching?: string[];
  additional?: any;
  id: string;
  from: number;
  till: number;
  showGuestsAlways?: boolean;
  /**
   * When true, suppress the guest-count suffix entirely. Used by the
   * `quandooTableView` experimental flag, which surfaces the guest count
   * (and name) in a separate label under the table instead.
   */
  hideGuests?: boolean;
  /** Compact rendering: smaller padding for multi-pill shared layout. */
  compact?: boolean;
  /**
   * When true, render as position:relative so the pill flows inside a
   * parent flex container (used for shared-table same-time clusters).
   */
  inline?: boolean;
  changed?: boolean;
  dragDisabled?: boolean;
  /**
   * When two reservation pills share a table (earlier anchored to `top`, later
   * to `bottom`), the enlarged finger grab area must not reach toward the other
   * pill — otherwise the two invisible drag targets overlap in the middle and,
   * because both pills share the same z-index, the later pill (rendered last)
   * wins the hit test and the wrong reservation gets dragged. `'top'` keeps the
   * upper pill from expanding downward, `'bottom'` keeps the lower pill from
   * expanding upward.
   */
  stackedSide?: 'top' | 'bottom';
};

const Time: React.FC<Props> = ({
  color: backgroundColor,
  touching = [],
  additional = '',
  textColor: color,
  time,
  top,
  bottom,
  left,
  right,
  size,
  guests,
  id,
  from,
  till,
  showGuestsAlways,
  hideGuests,
  compact,
  inline,
  changed,
  dragDisabled,
  stackedSide,
}) => {
  let fontSize = maxMin(size * 1.5, 8, 14);
  if (compact) fontSize = maxMin(size * 1.2, 7, 12);
  // Enlarged grab area around the pill for fingers, clamped so it cannot
  // reach over neighbouring tables and hijack their clicks or drags.
  const touchOffsetX = touchDevice ? maxMin(size * 2, 12, 24) : 4;
  const touchOffsetY = touchDevice ? maxMin(size * 2, 12, 20) : 4;
  // Collapse the inner-facing vertical expansion when two pills stack on one
  // table so their grab targets can't overlap (see `stackedSide`). The pill
  // still keeps a tiny inner cushion; the outer side stays finger-sized.
  const innerTouchOffsetY = 2;
  const topTouchOffsetY = stackedSide === 'bottom' ? innerTouchOffsetY : touchOffsetY;
  const bottomTouchOffsetY = stackedSide === 'top' ? innerTouchOffsetY : touchOffsetY;

  const { drag, opacity, preview } = useDrag<{
    id: string;
    from: number;
    till: number;
    guests: number;
    origin: 'floor-plan';
  }>(
    {
      id,
      from,
      till,
      guests: guests || 0,
      origin: 'floor-plan',
    },
    'reservation',
    dragDisabled,
  );

  return (
    <>
      {/* {!!preview && (
        <DragPreviewImage
          connect={preview}
          src={`data:image/svg+xml;utf8,<svg width="131" height="23" viewBox="0 0 131 23" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="131" height="23" rx="4" fill="#6369D1"/><text x="4" y="4" textLength="123" >${time} - ${blockToTime(
            till
          )} | ${guests} PAX</text></svg>`}
        />
      )} */}
      <span
        className={`time${changed ? ' changed' : ''}`}
        style={{
          ...(inline
            ? { position: 'relative', top: 'auto', bottom: 'auto', left: 'auto', right: 'auto' }
            : { top, bottom, left, right }),
          backgroundColor,
          color,
          fontSize,
          opacity,
          lineHeight: `${fontSize}px`,
          ...(compact ? { padding: '1px 3px' } : {}),
        }}
        ref={preview}
      >
        {!dragDisabled && (
          <span
            ref={drag}
            className="time-drag-touch-target"
            style={{
              position: 'absolute',
              left: -touchOffsetX,
              right: -touchOffsetX,
              top: -topTouchOffsetY,
              bottom: -bottomTouchOffsetY,
              borderRadius: touchOffsetY,
              cursor: 'grab',
              touchAction: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
          ></span>
        )}
        {time}
        {!hideGuests && (size > 7 || showGuestsAlways) ? ` ${guests} P.` : ''}
      </span>
    </>
  );
};

export default Time;
